/**
 * Notifications Hooks — canonical TanStack DB path.
 *
 * Compatibility layer preserving the historic `hooks/queries/useNotifications`
 * API surface while reads flow through `useLiveQuery` on the shared
 * user-scoped Electric collection and writes go through collection mutations
 * (`onInsert/onUpdate/onDelete` write-through handlers). No React Query cache
 * and no `notificationManager` calls remain in the browser path.
 *
 * Bulk operations (mark-all-read, clear-read) use server functions and
 * converge back into the collection via Electric sync.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { useCurrentOrganizationId } from '@/hooks/useCurrentOrganization';
import type { Notification, CreateNotificationData, FetchNotificationsOptions } from '@/types/notification';
import { toast } from '@/components/ui/sonner';
import { logger } from "@/lib/logger";
import { clearReadNotifications, markAllNotificationsRead } from "@/server/notifications";
import { getNotificationsCollection, useLiveQuery } from '@/lib/db';
import { setAccessToken } from '@/lib/db/auth-store';
import type { NotificationRow } from '@/lib/db';

// ============================================================================
// QUERY KEYS (retained for call-site cache-invalidation compatibility)
// ============================================================================

export const notificationKeys = {
  all: ['notifications'] as const,
  lists: () => [...notificationKeys.all, 'list'] as const,
  list: (userId: string, organizationId: string | null | undefined, options?: FetchNotificationsOptions) =>
    [...notificationKeys.lists(), userId, organizationId ?? 'none', options] as const,
  counts: () => [...notificationKeys.all, 'count'] as const,
  count: (userId: string, organizationId?: string | null) =>
    [...notificationKeys.counts(), userId, organizationId ?? 'none'] as const,
  detail: (id: string) => [...notificationKeys.all, 'detail', id] as const,
};

// ============================================================================
// TYPES
// ============================================================================

export interface NotificationsData {
  notifications: Notification[];
  count: number;
  hasMore: boolean;
}

function rowToNotification(row: NotificationRow): Notification {
  const data = (row.data ?? undefined) as Notification['data'];
  const title =
    data && typeof data === 'object' && !Array.isArray(data)
      ? (data as Record<string, unknown>).title
      : undefined;
  return {
    ...row,
    data,
    needs_multi_channel: row.needs_multi_channel ?? null,
    title: typeof title === 'string' ? title : undefined,
  } as Notification;
}

// ============================================================================
// QUERY HOOKS (collection-backed)
// ============================================================================

/**
 * Fetch notifications for the current user from the live Electric collection.
 *
 * @param options - Fetch options (includeRead, unreadOnly, limit, offset)
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useNotificationsQuery({ includeRead: false });
 * const notifications = data?.notifications || [];
 * ```
 */
export function useNotificationsQuery(
  options: (FetchNotificationsOptions & { unreadOnly?: boolean }) = { includeRead: true },
  _queryOptions?: unknown
) {
  const { user, session } = useAuth();
  const userId = user?.id ?? '';

  useEffect(() => {
    if (session?.access_token) setAccessToken(session.access_token);
  }, [session?.access_token]);

  const notifCol = useMemo(
    () => (userId ? getNotificationsCollection(userId) : null),
    [userId]
  );

  const emptyCol = useMemo(() => getNotificationsCollection(undefined), []);
  const col = notifCol ?? emptyCol;

  const { data: collectionRows } = useLiveQuery((q) =>
    q.from({ n: col }).select(({ n }) => n)
  );

  const result = useMemo((): NotificationsData => {
    const rows = (collectionRows ?? []) as NotificationRow[];
    let filtered = rows;
    if (options.unreadOnly || options.includeRead === false) {
      filtered = rows.filter((n) => !n.is_read);
    }
    const sorted = [...filtered].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
    const count = sorted.length;
    const offset = options.offset ?? 0;
    const limit = options.limit ?? 50;
    const page = sorted.slice(offset, offset + limit);
    return {
      notifications: page.map(rowToNotification),
      count,
      hasMore: offset + limit < count,
    };
  }, [collectionRows, options.unreadOnly, options.includeRead, options.offset, options.limit]);

  const refetch = useCallback(async () => ({ data: result }), [result]);

  return {
    data: userId ? result : { notifications: [], count: 0, hasMore: false },
    isLoading: !!userId && collectionRows === undefined,
    error: null as Error | null,
    refetch,
  };
}

/**
 * Unread notification count derived from the live collection.
 *
 * @example
 * ```tsx
 * const { data: unreadCount = 0 } = useUnreadCountQuery();
 * ```
 */
export function useUnreadCountQuery() {
  const { user, session } = useAuth();
  const userId = user?.id ?? '';

  useEffect(() => {
    if (session?.access_token) setAccessToken(session.access_token);
  }, [session?.access_token]);

  const notifCol = useMemo(
    () => (userId ? getNotificationsCollection(userId) : getNotificationsCollection(undefined)),
    [userId]
  );

  const { data: collectionRows } = useLiveQuery((q) =>
    q.from({ n: notifCol }).select(({ n }) => n)
  );

  const count = useMemo(() => {
    if (!collectionRows) return 0;
    return (collectionRows as NotificationRow[]).filter((n) => !n.is_read).length;
  }, [collectionRows]);

  return { data: count, isLoading: !!userId && collectionRows === undefined };
}

// ============================================================================
// MUTATION HOOKS (collection write-through)
// ============================================================================

/**
 * Mark one or more notifications as read — batched into a single collection
 * transaction so the write-through persists them together.
 */
export function useMarkAsReadMutation() {
  const { user } = useAuth();
  const notifCol = useMemo(
    () => getNotificationsCollection(user?.id),
    [user?.id]
  );

  return useMutation({
    mutationFn: async (notificationIds: string[]) => {
      if (!user?.id) throw new Error('User not authenticated');
      await notifCol.stateWhenReady();
      const now = new Date().toISOString();
      const existing = notificationIds.filter((id) => notifCol.get(id));
      if (existing.length === 0) return;
      await notifCol.update(existing, (drafts) => {
        for (const draft of drafts) {
          draft.is_read = true;
          draft.read_at = now;
        }
      });
    },
    onError: () => {
      toast.error('Error', { description: 'Failed to mark notification as read' });
    },
  });
}

/**
 * Mark all notifications as read — server bulk endpoint; Electric sync
 * streams the updates back into the collection.
 */
export function useMarkAllAsReadMutation() {
  const { user, session } = useAuth();

  return useMutation({
    mutationFn: async () => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }
      const result = await markAllNotificationsRead({
        data: { userId: user.id, accessToken: session?.access_token ?? '' },
      });
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onError: () => {
      toast.error('Error', { description: 'Failed to mark all notifications as read' });
    },
    onSuccess: () => {
      toast.success('Success', { description: 'All notifications marked as read' });
    },
  });
}

/**
 * Delete a notification through the collection.
 */
export function useDeleteNotificationMutation() {
  const { user } = useAuth();
  const notifCol = useMemo(
    () => getNotificationsCollection(user?.id),
    [user?.id]
  );

  return useMutation({
    mutationFn: async (notificationId: string) => {
      if (!user?.id) throw new Error('User not authenticated');
      await notifCol.stateWhenReady();
      if (!notifCol.get(notificationId)) return;
      await notifCol.delete(notificationId);
    },
    onError: () => {
      toast.error('Error', { description: 'Failed to delete notification' });
    },
  });
}

/**
 * Create a notification via collection insert.
 *
 * @example
 * ```tsx
 * const createNotification = useCreateNotificationMutation();
 * await createNotification.mutateAsync({
 *   user_id: 'user-123',
 *   type: 'TASK_ASSIGNED',
 *   message_preview: 'New task assigned',
 * });
 * ```
 */
export function useCreateNotificationMutation() {
  const { user } = useAuth();
  const notifCol = useMemo(
    () => getNotificationsCollection(user?.id),
    [user?.id]
  );

  return useMutation({
    mutationFn: async (data: CreateNotificationData) => {
      const id = crypto.randomUUID();
      // `title` isn't a column — carry it inside `data` like the legacy path did.
      const payload =
        data.title !== undefined
          ? { title: data.title, ...(typeof data.data === 'object' && data.data !== null ? data.data : {}) }
          : data.data;
      const row = {
        id,
        user_id: data.user_id,
        actor_id: data.actor_id ?? null,
        type: data.type,
        entity_type: data.entity_type ?? null,
        entity_id: data.entity_id ?? data.related_id ?? null,
        message_preview: data.message_preview ?? null,
        data: (payload ?? null) as Record<string, unknown> | null,
        is_read: false,
        read_at: null,
        created_at: new Date().toISOString(),
      } as NotificationRow;
      // Insert into the *recipient's* scope when it differs — the row won't
      // appear in their shape until sync, which is expected.
      const col = data.user_id === user?.id ? notifCol : getNotificationsCollection(data.user_id);
      await col.insert(row);
      return row;
    },
    onError: (error) => {
      logger.error('Failed to create notification:', { error });
      toast.error('Error', { description: 'Failed to create notification' });
    },
  });
}

/**
 * Clear all read notifications — server bulk delete; Electric sync streams
 * the deletes back into the collection.
 */
export function useClearReadNotificationsMutation() {
  const { user, session } = useAuth();

  return useMutation({
    mutationFn: async () => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      const accessToken = session?.access_token;
      if (!accessToken) {
        throw new Error('No access token available');
      }
      const result = await clearReadNotifications({
        data: { userId: user.id, accessToken },
      });
      if (result.error) {
        throw new Error(result.error);
      }
      return result.deletedCount;
    },
    onError: () => {
      toast.error('Error', { description: 'Failed to clear read notifications' });
    },
    onSuccess: () => {
      toast.success('Success', { description: 'Read notifications cleared' });
    },
  });
}
